import React from 'react';
import { motion } from 'motion/react';
import { COND_TIERS } from './helpers';
import { ZONE, COND_ABBR, findSkinImage, fmt, rise, ZoneHead, ZoneEmpty, ZoneFooter } from './zoneShared';
import { ItemPicker, PRESETS, SOURCES, usePricerBasket } from './pricerShared';

// Pricer Zone — the "Quote Desk": the same basket on the Overview 7 board.
// The percentage bar is the loud thing on the page because it is the one
// control that moves every number below it.

export default function PricerTabZone({
  skinsData = [], searchableOptions = [], livePrices, csfloatPrices,
  itemsWithConditions = new Set(),
}) {
  const b = usePricerBasket({ livePrices, csfloatPrices, itemsWithConditions });
  const under = b.askTotal <= b.marketTotal;

  return (
    <div className="zn-root">

      <ZoneHead
        pre="THE" hl="QUOTE" post="DESK"
        sub="Build a pile, name a percentage, and every line reprices at once."
        stickers={[
          b.lines.length
            ? { text: `${b.units} item${b.units !== 1 ? 's' : ''} on the desk`, bg: ZONE.teal }
            : { text: 'desk is empty', bg: ZONE.white },
          { text: `${b.sourceLabel} prices`, bg: ZONE.purple },
        ]}
      />

      {/* ── The desk: picker, percentage, source ───── */}
      <div className="zn-prc-desk">
        <div className="zn-prc-deskrow">
          <ItemPicker searchableOptions={searchableOptions} onPick={b.addItem}
            disabled={!searchableOptions.length} cls="zn-prc" />
          {b.lines.length > 0 && (
            <button className="zn-btn" onClick={b.clear}>Clear all</button>
          )}
        </div>

        <div className="zn-prc-dials">
          <div className="zn-prc-dial">
            <div className="zn-prc-cap">Percentage of market</div>
            <div className="zn-prc-pctrow">
              <input type="range" min="0" max="150" step="0.5" className="zn-prc-slider"
                value={Math.min(150, Math.max(0, b.globalPct))}
                onChange={e => b.setPctText(e.target.value)} />
              <div className="zn-prc-pctbox">
                <input value={b.pctText} onChange={e => b.setPctText(e.target.value)}
                  inputMode="decimal" aria-label="Percentage of market" />
                <span>%</span>
              </div>
            </div>
            <div className="zn-prc-chips">
              {PRESETS.map(p => (
                <button key={p} className={`zn-chip ${Math.abs(b.globalPct - p) < 0.001 ? 'on' : ''}`}
                  onClick={() => b.setPctText(String(p))}>{p}%</button>
              ))}
            </div>
          </div>

          <div className="zn-prc-dial">
            <div className="zn-prc-cap">Pricing source</div>
            <div className="zn-prc-chips">
              {SOURCES.map(s => {
                const avail = s.id === 'buff' ? !!livePrices : !!csfloatPrices;
                return (
                  <button key={s.id} className={`zn-chip ${b.source === s.id ? 'on' : ''}`}
                    disabled={!avail} onClick={() => b.setSource(s.id)}>
                    {s.label}{!avail ? ' (loading)' : ''}
                  </button>
                );
              })}
            </div>
            <div className="zn-prc-note">
              {b.loaded
                ? `Every line reads its own wear tier from ${b.sourceLabel}.`
                : `Waiting on ${b.sourceLabel} prices…`}
            </div>
          </div>
        </div>
      </div>

      {/* ── Lines ──────────────────────────────────── */}
      {b.lines.length === 0 ? (
        <ZoneEmpty>The desk is clear. Search above to put something on it.</ZoneEmpty>
      ) : (
        <div className="zn-prc-sheet">
          <div className="zn-prc-head">
            <span>Item</span>
            <span>Wear</span>
            <span className="r">Qty</span>
            <span className="r">Unit</span>
            <span className="r">At market</span>
            <span className="r">%</span>
            <span className="r">Your price</span>
            <span />
          </div>

          {b.rows.map((r, i) => {
            const img      = findSkinImage(skinsData, r.name, r.condition);
            const tiers    = b.pricedTiers(r.name);
            const hasTiers = tiers.length > 0 || itemsWithConditions.has(r.name);
            return (
              <motion.div key={r.id} className="zn-prc-line" {...rise(i)}>

                <div className="zn-prc-item">
                  {img
                    ? <img src={img} alt="" />
                    : <div className="zn-thumb-blank" style={{ width: 46, height: 32 }} />}
                  <span className="zn-prc-name" title={r.name}>{r.name}</span>
                </div>

                <div>
                  {hasTiers ? (
                    <select className="zn-prc-select" value={r.condition || ''}
                      onChange={e => b.patchLine(r.id, { condition: e.target.value || null })}>
                      <option value="">ANY</option>
                      {COND_TIERS.map(t => {
                        const quoted = tiers.some(x => x.value === t.value);
                        return (
                          <option key={t.value} value={t.value}>
                            {COND_ABBR[t.value] || t.value}{quoted ? '' : ' · n/a'}
                          </option>
                        );
                      })}
                    </select>
                  ) : (
                    <span className="zn-prc-dim">n/a</span>
                  )}
                </div>

                <div className="r">
                  <div className="zn-prc-qty">
                    <button onClick={() => b.patchLine(r.id, { qty: Math.max(1, r.qty - 1) })}
                      aria-label="One fewer">−</button>
                    <input value={r.qty} inputMode="numeric" aria-label="Quantity"
                      onChange={e => {
                        const n = parseInt(e.target.value.replace(/\D/g, ''), 10);
                        b.patchLine(r.id, { qty: isFinite(n) && n > 0 ? n : 1 });
                      }} />
                    <button onClick={() => b.patchLine(r.id, { qty: r.qty + 1 })}
                      aria-label="One more">+</button>
                  </div>
                </div>

                <div className="r zn-prc-num">
                  {r.unit == null ? <span className="zn-prc-dim">n/a</span> : `$${fmt(r.unit)}`}
                </div>
                <div className="r zn-prc-num">
                  {r.market == null ? <span className="zn-prc-dim">n/a</span> : `$${fmt(r.market)}`}
                </div>

                <div className="r">
                  {/* Blank means "follow the bar". A number here pins this one
                      line and takes it out of everything the bar moves. */}
                  <input className={`zn-prc-linepct${r.custom ? ' on' : ''}`}
                    value={r.pct} inputMode="decimal" aria-label={`Percentage for ${r.name}`}
                    placeholder={b.globalPct.toFixed(0)}
                    onChange={e => b.patchLine(r.id, { pct: e.target.value })} />
                </div>

                <div className="r zn-prc-num zn-prc-ask">
                  {r.ask == null ? <span className="zn-prc-dim">n/a</span> : `$${fmt(r.ask)}`}
                </div>

                <div className="r">
                  <button className="zn-prc-x" onClick={() => b.removeLine(r.id)} aria-label="Remove line">×</button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── The tote ───────────────────────────────── */}
      {b.lines.length > 0 && (
        <div className="zn-prc-tote">
          <motion.div className="zn-prc-tote-card" style={{ background: ZONE.white }} {...rise(0)}>
            <div className="zn-prc-tote-cap">At market</div>
            <div className="zn-prc-tote-val">${fmt(b.marketTotal)}</div>
            <div className="zn-prc-tote-sub">{b.units} item{b.units !== 1 ? 's' : ''} · {b.sourceLabel} at 100%</div>
          </motion.div>

          <motion.div className="zn-prc-tote-card big" style={{ background: ZONE.yellow }} {...rise(1)}>
            <div className="zn-prc-tote-cap">Your price</div>
            {/* Editable: type what the pile has to come to and the bar moves
                to whatever percentage that turns out to be. */}
            <div className="zn-prc-tote-val edit">
              <span>$</span>
              <input value={b.targetDraft ?? b.askTotal.toFixed(2)}
                inputMode="decimal" aria-label="Target total"
                onChange={e => b.applyTarget(e.target.value)}
                onBlur={() => b.setTargetDraft(null)}
                onKeyDown={e => { if (e.key === 'Enter') { b.setTargetDraft(null); e.currentTarget.blur(); } }} />
            </div>
            <div className="zn-prc-tote-sub">
              {b.effectivePct != null
                ? `$${fmt(b.askTotal)} at ${b.effectivePct.toFixed(1)}% of market`
                : 'no prices to work from yet'}
            </div>
          </motion.div>

          <motion.div className="zn-prc-tote-card" style={{ background: under ? ZONE.lime : ZONE.pink }} {...rise(2)}>
            <div className="zn-prc-tote-cap">Difference</div>
            {/* The gap itself, unsigned — "under market" below it is what says
                which way it runs, so a minus sign would say it twice. */}
            <div className="zn-prc-tote-val">${fmt(Math.abs(b.marketTotal - b.askTotal))}</div>
            <div className="zn-prc-tote-sub">
              {under ? 'under' : 'over'} market
              {b.unpriced > 0 && ` · ${b.unpriced} line${b.unpriced !== 1 ? 's' : ''} unpriced`}
            </div>
          </motion.div>
        </div>
      )}

      <ZoneFooter stickers={[
        { text: `prices via ${b.sourceLabel.toLowerCase()}`, bg: ZONE.purple },
        { text: 'blank % follows the bar', bg: ZONE.white },
        b.lines.length > 0
          ? { text: `${b.units} items ✦ ${(b.effectivePct ?? b.globalPct).toFixed(1)}%`, bg: ZONE.orange, right: true }
          : null,
      ]} />
    </div>
  );
}
